import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, KundeAkte, KundeStamm, Unternehmen } from "@kundeninfo/api-client";
import { BearbeitenLeiste, Feld, Modal, ModalFuss, darfAbbrechen, useBeforeUnload,
         useFormularZustand, useRecordLock, useBestaetigung } from "@kundeninfo/ui";
import { Auswahl, Formular, Hinweis } from "@kundeninfo/ui/me-design";
import type { SperrDienst } from "@kundeninfo/ui";
import { landName } from "../format.js";
import type { AircallSicht } from "../session.js";
import type { VerlassenWaechter } from "../router.js";
import { KundeBeziehungen } from "./KundeBeziehungen.js";
import { KundeAircallAccount } from "./KundeAircallAccount.js";
import { KundeKonditionen } from "./KundeKonditionen.js";
import { KundePortalMandanten } from "./KundePortalMandanten.js";
import "./KundeStammdaten.css";
import { KiKnopf } from "../components/meHuellen.js";
import { Ortszeile } from "../components/Ortszeile.js";
import { useLaender } from "../laender.js";
import { WebsiteLogoLookup, customerLogoSrc } from "./partners/LogoChoice.js";
import { LOGO_TEXT } from "./partners/texts.js";

/** Nur die Klientenmethoden, die diese Ansicht wirklich braucht — so lässt sie
 *  sich im Test mit einer Handvoll vi.fn zeichnen, ohne den ganzen ApiClient nachzubauen.
 *  Die drei mittleren gehören dem Abschnitt „Zahlungskonditionen" (Aufgabe 10), die drei
 *  danach dem Abschnitt „Portal-Mandanten" (Mandantenabgleich mit dem Monitor,
 *  07.09.2026). Beide stehen unter den Stammdatenfeldern und werden deshalb über diese
 *  Verdrahtung mitversorgt. */
export type StammdatenClient =
  Pick<ApiClient, "updateCustomer" | "acquireLock" | "refreshLock" | "releaseLock"
    | "listLaender"
    | "listSelectablePaymentTerms" | "getRelationTerms" | "setRelationTerms"
    | "listExternalReferences" | "createExternalReference" | "deleteExternalReference"
    // Der Abschnitt „Unternehmenszuordnung" (Kleinpakete II, P1): anlegen und danach
    // die Akte frisch holen — die Antwort des Schreibwegs trägt nur die Nummer.
    // `setDebitorennummer` kam mit der Debitorennummer je Beziehung dazu (Aufgabe 9).
    | "addRelation" | "getCustomer" | "setDebitorennummer"
    // Rollen je Unternehmen und Kreditornummer (Lieferanten Strang A, Aufgabe 6).
    | "setRelationRoles" | "setKreditornummer"
    // Die Rechnungsangaben je Beziehung (Migration 053, E342) — samt Neuanlage einer Adresse.
    | "getRelationBilling" | "setRelationBilling" | "listAddresses" | "createAddress" | "updateAddress"
    // Die Ortszeile (Spec „Adresszeile", R17) der Stammdaten selbst und des Adresspanels der Neuanlage; `listLaender` steht oben.
    | "listPlzOrte"
    // Der Abschnitt „Aircall-Anbindung" (Paket „Abrechnungsquellen", 29.09.2026; Leitungen seit Migration 051).
    | "getAircallAccount" | "setzeAircallAccount" | "aircallAnbindungLeitungen" | "ordneAircallLeitungZu"
    | "beendeAircallLeitung" | "widerrufeAircallZuordnung" | "aircallAuswirkung"
    // Logo aus der Webseite (Entscheid 673): Kacheln holen hinter dem Feld „Webseite“, das gewählte Logo beim Speichern.
    | "lookupEnrichment" | "uploadCustomerLogo">;

/**
 * Alles außer dem Kunden selbst: die Verdrahtung, die von App über KundenAkte
 * hierher durchgereicht wird. `onAktualisiert` meldet jeden neuen Serverstand nach
 * oben (nach dem Speichern wie nach einer Konfliktauflösung), damit Kopf und
 * Kontextspalte nicht auf einem veralteten Satz sitzen bleiben.
 */
export type StammdatenUmfeld = {
  client: StammdatenClient;
  /** Ob die Übernahme einer fremden Sperre angeboten wird — LEAD/ADMIN, entschieden
   *  vom Aufrufer (siehe darfSperreUebernehmen in session.ts). */
  darfUebernehmen: boolean;
  /** Darf dieser Benutzer den Aircall-Account pflegen? Nur ADMIN (R7, Spec
   *  „Abrechnungsquellen"). Weglassbar — ohne Angabe sieht niemand die Knöpfe. */
  istAdmin?: boolean | undefined;
  /** Wie der Aircall-Block erscheint (E365): `verbergen` ohne Block, `hinweis` mit dem Text
   *  zur Anbindung darüber (ADMIN außerhalb des Aircall-Unternehmens). */
  aircall: { sicht: AircallSicht; hinweis: string | null };
  onAktualisiert(akte: KundeAkte): void;
  registriereVerlassenWaechter(frage: VerlassenWaechter): () => void;
};

export type KundeStammdatenProps = StammdatenUmfeld & {
  kunde: KundeAkte;
  /**
   * Die sichtbaren Konzernunternehmen — nur für den Abschnitt „Unternehmenszuordnung"
   * unten. WAHLFREI: Ohne Liste gibt es nichts zuzuordnen, der Abschnitt zeigt dann nur
   * den Satz. So bleiben die Tests, die diese Ansicht ohne Sitzung zeichnen,
   * unverändert gültig.
   */
  companies?: readonly Unternehmen[];
};

/** Die Formularwerte — überall Zeichenketten, damit ein geleertes Feld erst beim
 *  Absenden zu null wird (der Schreibweg leert die Spalte dann wirklich). `country` ist
 *  nie leer (NOT-NULL-Spalte, Vorgabefall DE). */
type Entwurf = {
  companyName: string; street: string; houseNumber: string; postalCode: string;
  city: string;
  phone: string; email: string; vatId: string; industry: string; website: string;
  country: string; leitwegId: string; invoiceFormat: string;
};

// Reihenfolge wie in KundeAnlegen: die Webadresse zwischen E-Mail und Branche.
// `logoFile` fehlt hier — es ist kein Eingabefeld (siehe CustomerDetail in packages/db).
// `country`/`leitwegId`/`invoiceFormat` stehen NICHT in dieser Liste: Das Land steckt seit der
// Adresszeile in der Ortszeile, die an der Stelle der PLZ steht (der Ort steckt ebenfalls
// darin), das Rechnungsformat ist eine AUSWAHL, und die Leitweg-ID steht als E-Rechnungs-Feld
// neben dem Rechnungsformat am Ende der Maske — alle drei werden unten eigens gezeichnet.
const FELDER: { name: keyof Entwurf; label: string; typ?: "email" | "tel" }[] = [
  { name: "companyName", label: "Firmenname" },
  { name: "street", label: "Straße" },
  { name: "houseNumber", label: "Hausnummer" },
  { name: "postalCode", label: "PLZ" },
  { name: "city", label: "Ort" },
  { name: "phone", label: "Telefon", typ: "tel" },
  { name: "email", label: "E-Mail", typ: "email" },
  { name: "website", label: "Webseite" },
  { name: "vatId", label: "USt-IdNr." },
  { name: "industry", label: "Branche" },
];

/**
 * Die drei Rechnungsformate mit ihren deutschen Beschriftungen (Migration 025). Die
 * SCHLÜSSEL sind englisch und kommen aus dem Vertrag; die Wörter stehen hier, weil `web`
 * nichts aus `domain` importiert. Die Liste steht auch in RechnungDetail.tsx — vier
 * Zeilen zweimal sind besser als ein neuer geteilter Block für eine Beschriftung.
 */
const RECHNUNGSFORMATE: { wert: string; text: string }[] = [
  { wert: "PDF_A3", text: "PDF" },
  { wert: "ZUGFERD_EN16931", text: "ZUGFeRD (PDF mit XML)" },
  { wert: "XRECHNUNG_30", text: "XRechnung (XML)" },
];

/** ALLE Felder des Entwurfs mit Beschriftung — für den Gleichheitsvergleich, die
 *  Feldfehler-Zuordnung und den Konfliktvergleich. Eine zweite, kürzere Liste an einer
 *  dieser Stellen ließe die beiden 011er-Felder genau dort wieder herausfallen. */
const ALLE_FELDER: { name: keyof Entwurf; label: string }[] = [
  ...FELDER,
  { name: "country", label: "Land" },
  { name: "leitwegId", label: "Leitweg-ID" },
  { name: "invoiceFormat", label: "Rechnungsformat" },
];

function entwurfAus(akte: KundeAkte): Entwurf {
  return {
    companyName: akte.companyName,
    // `streetName`/`houseNumber` und NICHT `street`: `street` ist die fertige Zeile
    // („Hafenstraße 12a") und stünde im Straßenfeld mit der Hausnummer darin — beim
    // Speichern käme sie doppelt heraus.
    street: akte.streetName ?? "", houseNumber: akte.houseNumber ?? "",
    postalCode: akte.postalCode ?? "",
    city: akte.city ?? "",
    phone: akte.phone ?? "",
    email: akte.email ?? "",
    vatId: akte.vatId ?? "",
    industry: akte.industry ?? "",
    website: akte.website ?? "",
    country: akte.country,
    leitwegId: akte.leitwegId ?? "",
    invoiceFormat: akte.invoiceFormat ?? "PDF_A3",
  };
}

/**
 * IMMER alle Felder — auch `country`, `leitwegId` und `invoiceFormat`: Der Schreibweg ist
 * Vollersatz, und eine Maske, die sie nicht mitschickte, setzte das Land beim Speichern
 * still auf 'DE' zurück, leerte die Leitweg-ID und zöge das Rechnungsformat auf 'PDF_A3'
 * (genau die Übergangslage zwischen den Aufgaben dieses Pakets — seither geschlossen).
 */
function stammAus(entwurf: Entwurf): KundeStamm {
  const oderNull = (wert: string): string | null => (wert.trim() === "" ? null : wert);
  return {
    companyName: entwurf.companyName,
    street: oderNull(entwurf.street),
    houseNumber: oderNull(entwurf.houseNumber),
    postalCode: oderNull(entwurf.postalCode),
    city: oderNull(entwurf.city),
    phone: oderNull(entwurf.phone),
    email: oderNull(entwurf.email),
    vatId: oderNull(entwurf.vatId),
    industry: oderNull(entwurf.industry),
    website: oderNull(entwurf.website),
    country: entwurf.country,
    leitwegId: oderNull(entwurf.leitwegId),
    invoiceFormat:
      entwurf.invoiceFormat as "PDF_A3" | "ZUGFERD_EN16931" | "XRECHNUNG_30",
  };
}

function entwurfGleich(a: Entwurf, b: Entwurf): boolean {
  return ALLE_FELDER.every((f) => a[f.name] === b[f.name]);
}

/**
 * `conflict.current` kommt als unknown über die Leitung (die API kann seine Gestalt im
 * Schema nicht versprechen, siehe openapi.ts). Tatsächlich liefert der Fehlerhandler den
 * vollen Kundensatz; hier wird das geprüft statt blind behauptet — fehlt die Gestalt,
 * zeigt der Konfliktkasten nur die Meldung ohne Übernahmeangebot.
 */
function alsAkte(wert: unknown): KundeAkte | null {
  if (typeof wert !== "object" || wert === null) return null;
  const k = wert as Partial<KundeAkte>;
  if (typeof k.id !== "string" || typeof k.companyName !== "string"
    || typeof k.version !== "number") return null;
  return wert as KundeAkte;
}

const KONFLIKT_ZEIT = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
});

function formatKonfliktZeit(iso: string): string {
  const zeitpunkt = new Date(iso);
  if (Number.isNaN(zeitpunkt.getTime())) return iso;
  return `${KONFLIKT_ZEIT.format(zeitpunkt)} Uhr`;
}

type Konflikt = { von: string; wann: string; fremd: KundeAkte | null };

/** Was ein Speichern ergibt: der neue Stand und — nur wenn das gewählte Logo scheiterte — der Grund dafür. */
type SaveResult = { akte: KundeAkte; logoError: string | null };

/**
 * Der Inhalt des Tabs „Stammdaten" in beiden Modi — die erste Schreibfunktion des
 * Systems. Im Lesemodus stehen nur Werte (Feld mit bearbeitbar={false}); „Bearbeiten"
 * meldet den Wunsch bei useRecordLock an, und erst wenn die Sperre wirklich GEHALTEN
 * ist, öffnen sich die Felder. Schlägt der Erwerb mit 423 fehl, bleibt der Lesemodus
 * mit dem Hinweisband der Leiste stehen; der Haken beobachtet dann alle 15 s und
 * öffnet die Bearbeitung von selbst, sobald die fremde Sperre fällt (der Wunsch
 * bleibt ja angemeldet).
 *
 * Gegen verlorene Änderungen schützt der Versionsvergleich beim Speichern, nicht die
 * Sperre (Entscheidung AA) — deshalb wirft ein Speichern trotz gehaltener Sperre
 * unter Umständen eine 409, und deren Rumpf trägt den aktuellen Serverstand.
 *
 * **Wo die Bearbeitung steht** (Hausregel „striktes Modalpanel", Entscheid Dirk vom
 * 06.09.2026): im Panel aus @kundeninfo/ui, mit genau zwei Ausgängen in seiner Fußleiste.
 * Dahinter bleiben die Werte lesbar stehen — sie sind der Zusammenhang, in dem gerade
 * geändert wird.
 *
 * Die SPERRE wandert dabei nicht mit: Sie hängt weiterhin an `wunsch`, nicht am Panel.
 * Das ist der Grund, warum die Leiste zweimal vorkommt und nie zugleich — draussen im
 * Lesezustand (mit „Bearbeiten", dem FREMD-Band, „Übernehmen" und „Nicht mehr warten"),
 * drinnen während der Bearbeitung (dann NUR mit dem Störungsband, `aktionenImPanel`).
 * Der Wartezustand ist keine Bearbeitung: Dort steht kein Panel, und die Leiste draussen
 * behält jeden ihrer Wege.
 */
export function KundeStammdaten(p: KundeStammdatenProps): ReactElement {
  // Der zuletzt bekannte Serverstand — Quelle für Lesemodus UND für die Version beim
  // Speichern. Nach einem Erfolg steht hier die Antwort (mit ihrer neuen Version);
  // der Effekt hält ihn nach, wenn die Ebene darüber die Akte austauscht.
  const [stand, setStand] = useState(p.kunde);
  useEffect(() => { setStand(p.kunde); }, [p.kunde]);

  /** Der Benutzer will bearbeiten — treibt useRecordLock (aktiv) und bleibt auch bei
   *  FREMD bestehen, damit die Beobachtung weiterläuft. */
  const [wunsch, setWunsch] = useState(false);
  /** Die Felder sind offen. Wird erst wahr, wenn die Sperre GEHALTEN ist, und bleibt
   *  bei einem vorübergehenden Herzschlag-FEHLER bewusst wahr (Eingaben bleiben). */
  const [offen, setOffen] = useState(false);
  const [entwurf, setEntwurf] = useState<Entwurf>(() => entwurfAus(p.kunde));
  /** Das im Markenrechts-Dialog gewählte Logo (Entscheid 673) — Teil des Entwurfs, erst „Speichern“ lädt es hoch. */
  const [chosenLogo, setChosenLogo] = useState<string | null>(null);
  /** Meldung eines gescheiterten SPERR-Erwerbs (Lesezustand). Fehler des Speicherns führt
   *  `formular.meldung` (im Panel). */
  const [meldung, setMeldung] = useState<string | null>(null);
  const [konflikt, setKonflikt] = useState<Konflikt | null>(null);
  /** „Meine Eingaben behalten“ geht über `formular.neuLaden()` (der einzige Weg aus dem
   *  fremden Stand) — diese Markierung sagt dem Laden, dass es die Eingaben NICHT ersetzt,
   *  sondern nur die Versionsbasis vorrückt. */
  const eingabenBehalten = useRef(false);

  /**
   * Die Formularzustände (ME-Etappe 3, Aufgabe 3): `laeuft` sperrt die Felder während des
   * Speicherns, eine 422 legt die Befunde an die Felder (Sprung zum ersten), eine 409 setzt
   * das Formular in den „fremden Stand“ — gesperrt, die Eingaben bleiben stehen, daneben
   * der Vergleich beider Stände (Review-Fokus 2). Erst „Neu laden“ (fremden Stand
   * übernehmen) oder „Meine Eingaben behalten“ geben es wieder frei.
   */
  const formular = useFormularZustand<SaveResult>({
    speichern: async () => {
      let akte: KundeAkte;
      try {
        akte = await p.client.updateCustomer(stand.id, stand.version, stammAus(entwurf));
      } catch (fehler) {
        // Den mitgelieferten Serverstand der 409 festhalten — der Haken reicht ihn nicht
        // durch, der Vergleich und „Neu laden“ brauchen ihn.
        if (fehler instanceof ApiError && fehler.status === 409) {
          setKonflikt(fehler.conflict !== undefined
            ? { von: fehler.conflict.changedBy, wann: fehler.conflict.changedAt,
                fremd: alsAkte(fehler.conflict.current) }
            : { von: "", wann: "", fremd: null });
        }
        throw fehler;
      }
      // Das Logo NACH den Stammdaten (Entscheid 673): Eine 409 oben lädt nichts hoch. Scheitert nur das Logo, sind
      // die Stammdaten trotzdem gespeichert — das ist kein Fehler des Speicherns, sondern ein Ergebnis mit Grund
      // (Muster der Anlege-Maske); die Logo-Route springt die Version nicht, ihre Antwort ist der neue Stand.
      if (chosenLogo === null) return { akte, logoError: null };
      try {
        return { akte: await p.client.uploadCustomerLogo(akte.id, chosenLogo), logoError: null };
      } catch (fehler) {
        return { akte, logoError: fehler instanceof ApiError ? fehler.message : LOGO_TEXT.unknownError };
      }
    },
    neuLaden: async () => {
      const behalten = eingabenBehalten.current;
      eingabenBehalten.current = false;
      // Ohne mitgelieferten Stand wird der Kunde frisch geholt — auch für „Meine Eingaben
      // behalten“: Sonst rückte die Version nicht vor, und das nächste Speichern liefe
      // sofort wieder in die 409 (Fixrunde 1, H2).
      const fremd = konflikt?.fremd ?? await p.client.getCustomer(stand.id);
      setStand(fremd);
      p.onAktualisiert(fremd);
      // „Neu laden“ übernimmt den fremden Stand: Mit den Eingaben fällt auch die Logo-Wahl (Teil des Entwurfs).
      if (!behalten) { setEntwurf(entwurfAus(fremd)); setChosenLogo(null); }
      setKonflikt(null);
    },
    bekannteFelder: ALLE_FELDER.map((f) => f.name),
    // Sammelmeldung mit den sichtbaren Beschriftungen statt technischer Feldnamen
    // (Gesamtreview Etappe 3: einzige Maske der Stichprobe ohne Karte).
    beschriftungen: Object.fromEntries(ALLE_FELDER.map((f) => [f.name, f.label])),
  });
  const speichert = formular.laeuft;
  const feldFehler = formular.feldFehler as Partial<Record<keyof Entwurf, string>>;
  const laender = useLaender(p.client.listLaender);

  // Der Adapter auf den Client: die Fehlerform (423 mit locked: { lockedBy, since })
  // ist bereits deckungsgleich mit dem, was useRecordLock erwartet (Fixrunde 2, Fund 3
  // in useRecordLock.ts) — hier wird deshalb NUR benannt, nicht umgeformt. Die instabile
  // Objektidentität pro Render ist unschädlich: der Haken hält den Dienst in einer Ref.
  const dienst: SperrDienst = {
    acquire: (ziel, force) => p.client.acquireLock(ziel, force),
    refresh: (ziel) => p.client.refreshLock(ziel),
    release: (ziel) => p.client.releaseLock(ziel),
  };
  const { zustand, uebernehmen } = useRecordLock({
    dienst,
    ziel: { entityType: "customer", entityId: stand.id },
    aktiv: wunsch,
  });

  // Sobald die Sperre hält, öffnen sich die Felder — beim direkten Erwerb wie nach
  // einer Übernahme oder wenn die Beobachtung eine gefallene fremde Sperre ergattert.
  const sperrArt = zustand.art;
  useEffect(() => {
    if (wunsch && sperrArt === "GEHALTEN") setOffen(true);
  }, [wunsch, sperrArt]);

  // Scheitert schon der Erwerb technisch (nicht 423), wäre die Leiste stumm: ihr
  // FEHLER-Band zeigt sie nur während der Bearbeitung. Deshalb hier die Meldung und
  // zurück in den Ruhezustand — ein erneuter Klick auf „Bearbeiten" versucht es wieder.
  useEffect(() => {
    if (wunsch && !offen && zustand.art === "FEHLER") {
      setMeldung(zustand.meldung);
      setWunsch(false);
    }
  }, [wunsch, offen, zustand]);

  // Verlassen-Wächter: nur registriert, solange offene UND geänderte Eingaben da sind.
  // Er deckt programmatische Navigation und die Zurück-Taste gleichermaßen (useRoute).
  const geaendert = offen && (!entwurfGleich(entwurf, entwurfAus(stand)) || chosenLogo !== null);
  const { bestaetigen } = useBestaetigung();
  const registriere = p.registriereVerlassenWaechter;
  useEffect(() => {
    if (!geaendert) return;
    return registriere(() => bestaetigen({
      titel: "Seite verlassen?",
      text: "Es gibt ungespeicherte Änderungen. Sie gehen verloren.",
      bestaetigenText: "Verlassen",
      gefaehrlich: true,
    }));
  }, [geaendert, registriere]);

  // …und derselbe Stand für das ECHTE Verlassen (Tab zu, neu laden, App-Wechsel), das
  // der Wächter oben nicht sieht (2a-Kleinpunkt).
  useBeforeUnload(geaendert);

  function beiBearbeiten(): void {
    setEntwurf(entwurfAus(stand));
    setChosenLogo(null);
    setMeldung(null);
    formular.zuruecksetzen();
    setKonflikt(null);
    setWunsch(true);
  }

  /**
   * 2a-Kleinpunkt W1: Rückzug aus dem Wartezustand. Der Wunsch wird zurückgesetzt, damit
   * useRecordLock `aktiv:false` sieht — der Haken räumt daraufhin seine FREMD-Beobachtung
   * ab und geht nach AUS (belegt in useRecordLock.test.tsx, „hört auf zu beobachten, wenn
   * der Wunsch zurückgenommen wird"). Ohne dieses Zurücksetzen liefe die Beobachtung
   * weiter und risse dem Benutzer später ungefragt die Bearbeitung auf.
   *
   * Bewusst derselbe Rückbau wie beim Verwerfen: Im Wartezustand ist nichts geöffnet und
   * nichts eingegeben, der Unterschied läge allein im Namen.
   */
  function beiNichtMehrWarten(): void {
    beiVerwerfen();
  }

  /**
   * Der EINE Ausgang des Panels neben dem Speichern (Hausregel „striktes Modalpanel",
   * 06.09.2026): kein Ausgang unter laufendem Schreiben, Rückfrage bei ungespeicherten
   * Eingaben. Knopf und Escape gehen durch diese Funktion.
   *
   * Dahinter steckt derselbe Rückbau wie beim früheren „Verwerfen" — samt der Freigabe der
   * Sperre. Verlorenginge sonst nicht nur die Eingabe: Ein Abbrechen, das die Sperre stehen
   * liesse, sperrte den Datensatz für alle anderen aus.
   */
  async function abbrechen(): Promise<void> {
    if (speichert) return;
    if (!(await darfAbbrechen(geaendert))) return;
    beiVerwerfen();
  }

  function beiVerwerfen(): void {
    setEntwurf(entwurfAus(stand));
    setChosenLogo(null);
    setMeldung(null);
    formular.zuruecksetzen();
    setKonflikt(null);
    setOffen(false);
    setWunsch(false); // gibt die Sperre über useRecordLock frei
  }

  async function beiSpeichern(): Promise<void> {
    const ergebnis = await formular.aktion();
    if (ergebnis === undefined) return; // Befund steht am Feld, im Hinweis oder im Formular
    const antwort = ergebnis.akte;
    // Version und Werte aus der ANTWORT übernehmen — mit der alten Version
    // scheiterte das nächste Speichern sofort am Versionsvergleich.
    setStand(antwort);
    setEntwurf(entwurfAus(antwort));
    setChosenLogo(null);
    setKonflikt(null);
    setOffen(false);
    setWunsch(false); // Sperre frei
    // Nur das Logo scheiterte: Die Meldung steht im Lesezustand, wo das Panel eben war.
    if (ergebnis.logoError !== null) setMeldung(LOGO_TEXT.savedWithoutLogo(ergebnis.logoError));
    p.onAktualisiert(antwort);
  }

  /** Konfliktauflösung 2: die eigene Eingabe behalten. Die Versionsbasis rückt auf den
   *  Serverstand vor — das nächste Speichern überschreibt den fremden Stand dann
   *  BEWUSST (der Hinweis hat beide Stände gezeigt). Konfliktauflösung 1 („Neu laden“,
   *  fremden Stand übernehmen) ist der Knopf in der Hinweiszeile des Formulars. */
  function eigeneEingabenBehalten(): void {
    eingabenBehalten.current = true;
    void formular.neuLaden();
  }

  const modus = offen ? (speichert ? "SPEICHERT" : "BEARBEITEN") : "LESEN";
  const leseWerte = entwurfAus(stand);
  const fremdeWerte = konflikt !== null && konflikt.fremd !== null
    ? entwurfAus(konflikt.fremd) : null;

  /**
   * Die Leiste — mit ihren Rückrufen an einer Stelle, weil sie an ZWEI Orten vorkommt:
   * draussen im Lesezustand und drinnen im Panel (siehe Kopfkommentar). Nie zugleich.
   */
  function leiste(imPanel: boolean): ReactElement {
    return (
      <BearbeitenLeiste
        modus={modus}
        sperre={zustand}
        darfUebernehmen={p.darfUebernehmen}
        {...(imPanel ? { aktionenImPanel: true } : {})}
        onBearbeiten={beiBearbeiten}
        onSpeichern={() => { void beiSpeichern(); }}
        onVerwerfen={beiVerwerfen}
        onUebernehmen={() => { void uebernehmen(); }}
        onNichtMehrWarten={beiNichtMehrWarten}
      />
    );
  }

  /**
   * Die Meldung gehört dorthin, wo gerade gearbeitet wird: Ein gescheiterter SPERR-Erwerb
   * steht im Lesezustand, ein gescheitertes SPEICHERN im Panel — dort, wo die Eingabe
   * steht, die stehen geblieben ist. Hinter dem Schleier wäre sie ein Alarm, auf den
   * niemand reagieren kann.
   */
  function meldungsband(text: string | null): ReactElement | null {
    if (text === null) return null;
    return <Hinweis art="fehler">{text}</Hinweis>;
  }

  return (
    <div className="ks">
      {!offen && leiste(false)}
      {!offen && meldungsband(meldung)}

      {/* Die Werte stehen IMMER lesbar da — auch hinter dem offenen Panel: Sie sind der
          Zusammenhang, in dem gerade geändert wird. Im Lesemodus zeichnet `Feld` sie als
          reinen Text, ohne `<input>` und ohne `id` — deshalb kollidieren sie mit den
          Eingabefeldern des Panels weder in der Kennung noch für den Bildschirmleser. */}
      <div className="ks__felder">
        {FELDER.map((f) => (
          <Feld key={f.name} id={`ks-lesen-${f.name}`} label={f.label}
            wert={leseWerte[f.name]} bearbeitbar={false} onChange={() => {}} />
        ))}
        <Feld id="ks-lesen-country" label="Land" bearbeitbar={false}
          wert={landName(leseWerte.country)} onChange={() => {}} />
        <Feld id="ks-lesen-leitwegId" label="Leitweg-ID" bearbeitbar={false}
          wert={leseWerte.leitwegId} onChange={() => {}} />
        <Feld id="ks-lesen-invoiceFormat" label="Rechnungsformat" bearbeitbar={false}
          wert={RECHNUNGSFORMATE.find((f) => f.wert === leseWerte.invoiceFormat)?.text
            ?? leseWerte.invoiceFormat}
          onChange={() => {}} />
      </div>

      {/* Die Zahlungskonditionen stehen UNTER den Stammdaten, nicht zwischen ihnen: Sie
          gehören der Kundenbeziehung, nicht der Firma, und werden getrennt gespeichert
          (eigene Version, eigener Schreibweg) — die Bearbeitung oben fasst sie nicht an.
          Gezeigt wird `stand`, nicht `p.kunde`: Nach einem Speichern der Stammdaten steht
          dort der frische Serverstand. */}
      <KundeKonditionen client={p.client} relations={stand.relations} verdeckt={offen} />

      {/* Und darunter die PORTAL-MANDANTEN — ein EIGENER Block, kein Teil der Stammdaten:
          Die Zuordnungen sind eigene Sätze, die angelegt und entfernt werden, nicht
          mitgespeichert. Sie hängen deshalb nicht am Speichern-Knopf oben und brauchen
          keine Satzsperre (zwei Bearbeiter kommen sich nicht in die Quere; meinen sie
          denselben Mandanten, entscheidet der eindeutige Schlüssel in der Datenbank).

          `stand.id` und nicht `p.kunde.id`: derselbe Grund wie beim Konditionen-Abschnitt
          darüber — nach einem Speichern steht in `stand` der frische Serverstand. */}
      <KundePortalMandanten client={p.client} customerId={stand.id} verdeckt={offen} />

      {/* Der AIRCALL-ACCOUNT (Spec „Abrechnungsquellen") — ein EIGENER Block neben den
          Portal-Mandanten, aus demselben Grund: ein eigener Satz mit eigener Version,
          nicht Teil der Stammdaten. Pflegen darf nur ADMIN; lesen jeder im Aircall-Unternehmen,
          ADMIN überall mit Hinweis auf die Anbindung (Mandantentrennung E365). */}
      {p.aircall.sicht === "hinweis" && p.aircall.hinweis !== null
        && <Hinweis art="info">{p.aircall.hinweis}</Hinweis>}
      {p.aircall.sicht !== "verbergen" && (
        <KundeAircallAccount client={p.client} customerId={stand.id} leitungenClient={p.client}
          kundeName={stand.companyName} istAdmin={p.istAdmin === true} verdeckt={offen} />
      )}

      {/* Und darunter die UNTERNEHMENSZUORDNUNG (Kleinpakete II, P1) — wieder ein
          EIGENER Block aus demselben Grund wie die beiden darüber: Eine Kundenbeziehung
          ist ein eigener Satz, der angelegt wird, nicht ein Feld, das mitgespeichert
          wird. `stand` und nicht `p.kunde`: Nach einem Speichern der Stammdaten steht
          dort der frische Serverstand. */}
      <KundeBeziehungen client={p.client} kunde={stand} companies={p.companies ?? []}
        verdeckt={offen}
        onAktualisiert={(akte) => { setStand(akte); p.onAktualisiert(akte); }} />

      {offen && (
        <Modal titel={`Stammdaten von ${stand.companyName} bearbeiten`} breite="mittel"
          onAbbrechen={abbrechen} abbrechenGesperrt={speichert}>
          {leiste(true)}
          {meldungsband(formular.meldung)}
          <Formular beschriftung="oben" {...formular.formularProps} hinweis={konfliktKasten()}>
            {eingabeFelder()}
          </Formular>
          {/* Die zwei einzigen Ausgänge, unten am Panel. „Abbrechen" statt des früheren
              „Verwerfen": Der Rahmen spricht in jeder Maske dieselben zwei Wörter — und
              getan wird dasselbe (Eingabe zurück, Sperre frei), nur mit Rückfrage.

              SPEICHERN IST GESPERRT, SOLANGE DIE SPERRE FREMD IST (Ruling zur Fixrunde 1,
              07.09.2026). Eine bewusste VERSCHÄRFUNG gegenüber Entscheidung AA: Dort
              schützt der Versionsvergleich und nicht die Sperre — die Sperre bleibt eine
              Höflichkeit, die Version die Garantie, und daran ändert sich nichts. Aber
              neben einem Band, das „X bearbeitet diesen Datensatz" sagt, gehört kein
              scharfer Speichern-Knopf: Er lüde ein, genau das zu tun, wovon das Band abrät.
              Wer trotzdem muss, ÜBERNIMMT die Sperre (dann ist sie nicht mehr fremd) — oder
              bricht ab. */}
          <ModalFuss speichernText="Speichern"
            speichernGesperrt={formular.zustand !== "bereit" || zustand.art === "FREMD"}
            abbrechenGesperrt={speichert}
            onSpeichern={() => { void beiSpeichern(); }} onAbbrechen={abbrechen} />
        </Modal>
      )}
    </div>
  );

  /**
   * Der Text der Hinweiszeile „fremder Stand“ (Slot `hinweis` des Formulars): wer wann
   * geändert hat, der Vergleich beider Stände (sichtbare Zusammenfassung — nichts geht
   * still verloren, Review-Fokus 2) und der zweite Weg „Meine Eingaben behalten“. Der erste
   * Weg, den fremden Stand zu übernehmen, ist „Neu laden“ des Formulars.
   */
  function konfliktKasten(): ReactElement | undefined {
    if (konflikt === null) return undefined;
    return (
      <div>
        {konflikt.von !== ""
          ? <>{konflikt.von} hat diesen Kunden inzwischen geändert
              ({formatKonfliktZeit(konflikt.wann)}).</>
          : <>Dieser Kunde wurde inzwischen geändert.</>}
        {" "}Deine Eingaben sind nicht verloren — vergleiche beide Stände und entscheide.
        {fremdeWerte !== null && (
          <dl className="ks__konflikt-vergleich">
            {/* ALLE elf Felder — stünde hier nur FELDER, verschwände ein Konflikt an
                Land oder Leitweg-ID unsichtbar aus dem Vergleich. Das Land erscheint hier
                mit seinem deutschen Namen (die Ortszeile selbst zeigt nur das Kürzel). */}
            {ALLE_FELDER.filter((f) => fremdeWerte[f.name] !== entwurf[f.name])
              .map((f) => {
                const anzeige = (wert: string): string => {
                  if (wert.trim() === "") return "—";
                  if (f.name === "country") return landName(wert);
                  if (f.name === "invoiceFormat") {
                    return RECHNUNGSFORMATE.find((r) => r.wert === wert)?.text ?? wert;
                  }
                  return wert;
                };
                return (
                  <div key={f.name}>
                    <dt>{f.label}</dt>
                    <dd>Deine Eingabe: {anzeige(entwurf[f.name])}</dd>
                    <dd>Aktueller Stand: {anzeige(fremdeWerte[f.name])}</dd>
                  </div>
                );
              })}
          </dl>
        )}
        {" "}
        <KiKnopf className="ki-eingaben-behalten" onClick={eigeneEingabenBehalten}>
          Meine Eingaben behalten
        </KiKnopf>
      </div>
    );
  }

  /**
   * Die EINGABEfelder — nur im Panel. Bewusst eine Funktion, die JSX zurückgibt, und keine
   * eigene Komponente: Eine im Render definierte Komponente bekäme bei jedem Neuzeichnen
   * eine neue Identität, React hängte ihren Teilbaum ab und neu auf, und der Fokus spränge
   * nach jedem getippten Zeichen aus dem Feld (Lehre aus KundeAnsprechpartner).
   */
  function eingabeFelder(): ReactElement {
    return (
      <>
        {FELDER.map((f) => {
          // Land, PLZ und Ort in EINER Zeile (Spec „Adresszeile", E400); der Ort steckt in der Zeile.
          // Der `key` bindet den inneren Stand der Zeile an Datensatz UND Fassung: „Neu laden“ und
          // „Meine Eingaben behalten“ rücken die Version vor und beginnen die Zeile neu (M-6a).
          if (f.name === "city") return null;
          if (f.name === "postalCode") {
            return (
              <Ortszeile key={`ortszeile:${stand.id}:${stand.version}`} praefix="ks"
                laender={laender} plzOrte={p.client.listPlzOrte}
                gesperrt={formular.zustand !== "bereit"}
                werte={{ country: entwurf.country, postalCode: entwurf.postalCode, city: entwurf.city }}
                fehler={feldFehler}
                onAenderung={(teil) => setEntwurf((e) => ({ ...e, ...teil }))} />
            );
          }
          const fehlerText = feldFehler[f.name];
          const feld = (
            <Feld
              key={f.name}
              id={`ks-${f.name}`}
              label={f.label}
              wert={entwurf[f.name]}
              bearbeitbar
              onChange={(wert) => setEntwurf((e) => ({ ...e, [f.name]: wert }))}
              {...(f.typ !== undefined ? { typ: f.typ } : {})}
              {...(fehlerText !== undefined ? { fehler: fehlerText } : {})}
            />
          );
          // Hinter der Webseite der Knopf „Logo aus der Webseite“, darunter die Kacheln (Entscheid 673).
          if (f.name !== "website") return feld;
          return (
            <WebsiteLogoLookup key={f.name} website={entwurf.website} lookup={p.client.lookupEnrichment}
              name="ks-logo" chosen={chosenLogo} onChosen={setChosenLogo}
              existingLogo={customerLogoSrc(stand)}>
              {feld}
            </WebsiteLogoLookup>
          );
        })}

        {/* Leitweg-ID (BT-10), Migration 011 — das Land steht seit der Ortszeile oben (Spec „Adresszeile“).
            Das Rechnungsformat steht NEBEN der Leitweg-ID: Sie gehören zusammen —
            die eine ist die Bedingung der anderen (XRechnung verlangt sie bei B2G). */}
        <Auswahl id="ks-invoiceFormat" name="ks-invoiceFormat" beschriftung="Rechnungsformat"
          wert={entwurf.invoiceFormat}
          {...(feldFehler.invoiceFormat !== undefined
            ? { fehler: feldFehler.invoiceFormat } : {})}
          onGeaendert={(d) => setEntwurf((e) => ({ ...e, invoiceFormat: d.wert }))}>
          {RECHNUNGSFORMATE.map((f) => (
            <option key={f.wert} value={f.wert}>{f.text}</option>
          ))}
        </Auswahl>
        {/* Der Hinweis erscheint bei der Wahl und nicht erst als 422 nach dem
            Speichern: Wer XRechnung wählt, soll die Bedingung SEHEN, solange das Feld
            darunter noch leer ist. Die Abweisung bleibt trotzdem — der Server prüft,
            was er speichert. */}
        {entwurf.invoiceFormat === "XRECHNUNG_30" && entwurf.leitwegId.trim() === "" && (
          <Hinweis art="info">
            XRechnung braucht eine Leitweg-ID — bitte im Feld darunter erfassen.
          </Hinweis>
        )}
        <Feld
          id="ks-leitwegId"
          label="Leitweg-ID"
          wert={entwurf.leitwegId}
          bearbeitbar
          onChange={(wert) => setEntwurf((e) => ({ ...e, leitwegId: wert }))}
          {...(feldFehler.leitwegId !== undefined
            ? { fehler: feldFehler.leitwegId } : {})}
        />
      </>
    );
  }
}
